// ink-component
import React from 'react';

import FormattedNumber, { FormattedNumberProps } from '../FormattedNumber/FormattedNumber';
import getNumberValue from '../_common/helpers/getNumberValue';

export interface FormattedBigCurrencyProps extends Omit<FormattedNumberProps, 'format' | 'rounding'> {
  /** Enforce a magnitude on the formatted number, regardless of it's size */
  strictMagnitude?: 'm' | 't' | 'b' | 'k';
  /** Number of extra decimal places beyond the first, for stricter formatting */
  extraDecimals?: 0 | 1 | 2 | 3 | 4;
}

/**
 * @purpose Represents the value into a more readable order of magnitude
 * @group Formatted
 * @categories formatter
 */
function FormattedBigCurrency(props: FormattedBigCurrencyProps) {
  const { strictMagnitude = '', extraDecimals = 1, value } = props;
  const adjustedValue = getNumberValue(value);

  // Values under $1,000 end up looking like a bug when mixed with figures > $1,000. E.g.,
  // $100.1
  // $225.3K
  // Eliminating decimals (dropping precision) reduces confusion
  let clampedExtraDecimals = extraDecimals.valueOf();
  if (adjustedValue) {
    if (adjustedValue > 0 && adjustedValue < 1_000) {
      clampedExtraDecimals = 0;
    }
  }
  const decimals = '0'.repeat(clampedExtraDecimals);
  return <FormattedNumber {...props} format={`0.[${decimals}]a${strictMagnitude}`} />;
}

FormattedBigCurrency.defaultProps = {
  value: undefined,
  prefix: '$',
  suffix: '',
  capitalize: true,
  strictMagnitude: undefined,
  extraDecimals: undefined,
  'data-testid': undefined,
};

FormattedBigCurrency.displayName = 'FormattedBigCurrency';

export default FormattedBigCurrency;
